// src/pages/RelationshipCard.js
//
// Mobile card for relationship / location list tabs (2026-10-28). Below
// 768px these tabs render one card per row instead of the wide table, which
// otherwise needs a sideways swipe to reach the location and count columns.
// Same panel colours as ShowArchiveCard.js (the Show Archive mobile card).
//
// Layout: name (linked) and detail lines on the left, count badge(s) on the
// right. The right column is omitted when no counts are passed (Festivals
// Played, which is a per-show list rather than a tally).
//
// Purely presentational — each tab passes its own values; search, pagination
// and desktop tables are untouched.
//
// Used by: ArtistDetail (Artist / Promoter Relationships, Festivals Played,
// Venues Played), FestivalDetail (Venues, Artists Hosted), PromoterDetail
// (Artist / Venue Relationships), VenueDetail (Artists Hosted, Promoter
// Relationships), ShowStatsTabs (Artists Watched, Venues Visited — MyShows
// and PublicProfile).

import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Props:
 *   title      node   — first line, e.g. the artist or venue name (required)
 *   titleTo    string — link target for the title
 *   titleMeta  node   — muted text after the title on the same line
 *                        (e.g. "Capacity: 1,200")
 *   lines      node[] — further lines under the title; blanks are skipped
 *   counts     { value, label }[] — badges stacked in the right column
 */
export default function RelationshipCard({ title, titleTo, titleMeta, lines, counts }) {
    const shownLines = (lines || []).filter(l => l !== null && l !== undefined && l !== false && l !== '');
    const shownCounts = (counts || []).filter(Boolean);

    return (
        <div style={S.card}>
            <div style={S.left}>
                <div style={S.titleLine}>
                    {titleTo
                        ? <Link to={titleTo} style={S.titleLink}>{title}</Link>
                        : <span style={S.titleText}>{title}</span>}
                    {titleMeta && <span style={S.titleMeta}>{titleMeta}</span>}
                </div>
                {shownLines.map((line, i) => (
                    <div key={i} style={S.line}>{line}</div>
                ))}
            </div>
            {shownCounts.length > 0 && (
                <div style={S.right}>
                    {shownCounts.map((c, i) => (
                        <div key={i} style={S.countBlock}>
                            <span style={S.countBadge}>{c.value}</span>
                            {c.label && <span style={S.countLabel}>{c.label}</span>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// Thin wrapper so tabs don't each need list spacing styles.
export function RelationshipCardList({ children }) {
    return <div style={S.list}>{children}</div>;
}

// Joins city / state / country, skipping blanks.
export function joinLocation(...parts) {
    return parts.filter(p => p && String(p).trim()).join(', ');
}

// "Capacity: 1,200", or null when capacity is unknown.
export function capacityLabel(capacity) {
    if (capacity === null || capacity === undefined || capacity === '') return null;
    const n = Number(capacity);
    if (isNaN(n)) return null;
    return `Capacity: ${n.toLocaleString()}`;
}

// Secondary-line styles for callers that compose their own line JSX.
export const relCardText = {
    link: { color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
    muted: { color: '#aaa' },
    strong: { color: '#e0e0e0' },
    lineupLabel: { color: '#aaa', fontWeight: 'normal' },
    lineup: { color: '#e0e0e0', fontWeight: 'bold', overflowWrap: 'anywhere' },
};

const S = {
    list: { display: 'flex', flexDirection: 'column', gap: '10px' },
    card: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        backgroundColor: '#1e1e1e',
        border: '1px solid #2e2e2e',
        borderRadius: '8px',
        padding: '12px 14px',
    },
    left: { flex: 1, minWidth: 0 },
    titleLine: { fontSize: '15px', lineHeight: '1.4', overflowWrap: 'anywhere', marginBottom: '2px' },
    titleLink: { color: '#bb86fc', fontWeight: 'bold', textDecoration: 'none' },
    titleText: { color: '#bb86fc', fontWeight: 'bold' },
    titleMeta: { color: '#888', fontSize: '13px', fontWeight: 'normal', marginLeft: '8px', whiteSpace: 'nowrap' },
    line: { fontSize: '14px', color: '#aaa', lineHeight: '1.4', marginTop: '2px', overflowWrap: 'anywhere' },
    right: {
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        minWidth: '64px',
    },
    countBlock: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' },
    countBadge: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '6px',
        padding: '4px 10px',
        fontSize: '15px',
        fontWeight: 'bold',
        color: '#bb86fc',
        lineHeight: '1.2',
    },
    countLabel: {
        fontSize: '10px',
        color: '#888',
        textTransform: 'uppercase',
        letterSpacing: '0.4px',
        textAlign: 'center',
        maxWidth: '80px',
        lineHeight: '1.2',
    },
};
